import React, {useState} from "react";
import {useNavigate} from "react-router-dom";
import {useIntl} from "react-intl";
import auth from "../../../../utils/authentication";
import useUserPreferences from "../../../user-app-config/hooks/use-user-preferences";
import UseSquadflixItemsRows, {SquadflixItemRowId} from "../../squadflixHome/hooks/useSquadflixItemsRows";
import useTrendingHeaderItems from "../../hooks/useTrendingHeaderItems";
import UseSquadflixNavigate from "../../squadflixHome/useSquadflixNavigate";
import {
    useCollectionItemsData,
    useContinueWatchingData,
    useNewEpisodesData,
    useSpanishLeagueEventsData,
    useSquadflixOriginalsData,
    useStremioChannelsData,
    useTorrestreamItemsData,
    useTrendingMoviesData,
    useTrendingSeriesData
} from "../../hooks/useCatalogRowData";
import {
    isLeagueGameStarted,
    leagueGameStartsToday
} from "../../hooks/useLiveChannelDetailsData";
import {
    countItemTotalSeasons,
    dateIsToday,
    getLocalDateTimeString,
    getSerieDates,
    getYearOfDate
} from "../../../../utils/utils";
import {LeagueEvent} from "../../../../apis/towers-sports-api/types";
import TmdbImage from "../../../shared/tmdb-image/tmdb-image";
import TmdbCollectionSelector from "../../movies/tmdbCollectionSelector/tmdbCollectionSelector";
import TmdbCredits from "../../shared/tmdb-credits/tmdb-credits";
import {useChatStore} from "../../../chatbot/store/chatbotStore";
import TvMediaRow from "../components/TvMediaRow";
import TvMediaCard from "../components/TvMediaCard";
import TvModal from "../components/TvModal";

type RowCommonProps = {
    rowId: string;
    order: number;
    showReorderControls: boolean;
    isFirst: boolean;
    isLast: boolean;
    onMoveUp: () => void;
    onMoveDown: () => void;
};

const ContinueWatchingTvRow: React.FC<RowCommonProps> = (props) => {
    const {episodes, loading} = useContinueWatchingData();
    const squadflixNav = UseSquadflixNavigate({});
    const {userPreferences, loading: loadingPreferences} = useUserPreferences();
    const intl = useIntl();

    if (loading || !episodes.length) return null;

    return (
        <TvMediaRow
            {...props}
            title={intl.formatMessage({id: "squadflix-home.continue-watching"})}
            icon={<i className="fa-solid fa-play-circle"></i>}
            count={episodes.length}>
            {episodes.map((item: any) => {
                const isSpoiler =
                    (userPreferences.hideSpoilers || userPreferences.hideSpoilers == null || loadingPreferences) &&
                    !item.view;
                return (
                    <TvMediaCard
                        key={item.id}
                        width="350px"
                        aspectRatio="16/9"
                        tmdbSrc={item.serie?.backdrop_path || item.still_path}
                        showPlayOverlay
                        meta="Continuar viendo"
                        title={item.serie?.name}
                        subtitle={
                            isSpoiler
                                ? `Temporada ${item.season_number} • Episodio ${item.episode_number}`
                                : `S${item.season_number}:E${item.episode_number} — ${item.name}`
                        }
                        badgeTopLeft={`S${item.season_number}:E${item.episode_number}`}
                        onClick={() => squadflixNav.navigateToEpisode(item)}
                        onSecondaryAction={() => squadflixNav.navigateToSerie(item.serie)}
                        secondaryActionLabel="Ir a la serie"
                    />
                );
            })}
        </TvMediaRow>
    );
};

const SpanishLeagueEventsTvRow: React.FC<RowCommonProps> = (props) => {
    const {events, loading} = useSpanishLeagueEventsData();
    const navigate = useNavigate();
    const [selectedEvent, setSelectedEvent] = useState<LeagueEvent | null>(null);

    if (loading || !events.length) return null;

    return (
        <>
            <TvMediaRow
                {...props}
                title="Fútbol España En Vivo"
                icon={<i className="fa-solid fa-futbol"></i>}
                count={events.length}>
                {events.map((item) => {
                    const started = isLeagueGameStarted(item);
                    const today = leagueGameStartsToday(item);
                    const gameDate = getLocalDateTimeString(item.strTimestamp + "Z").slice(0, -3);
                    const timeOnly = gameDate.split(" ")[1];
                    const hasLinks = !!item.acestreamEvent?.enlaces?.length;

                    return (
                        <TvMediaCard
                            key={item.idEvent}
                            width="350px"
                            aspectRatio="16/9"
                            cachedSrc={item.strThumb}
                            showPlayOverlay={started && hasLinks}
                            badgeTopLeft={
                                started ? (
                                    <span className="tv-badge-live">● EN VIVO</span>
                                ) : today ? (
                                    `Hoy ${timeOnly}`
                                ) : (
                                    "Próximamente"
                                )
                            }
                            badgeBottomRight={
                                hasLinks
                                    ? `${item.acestreamEvent.enlaces.length} enlaces`
                                    : started
                                    ? "Sin enlaces"
                                    : undefined
                            }
                            meta={started ? "Partido en directo" : "Próximo encuentro"}
                            title={item.strEvent}
                            subtitle={gameDate}
                            onClick={() => {
                                if (!hasLinks) return;
                                if (item.acestreamEvent.enlaces.length === 1 && started) {
                                    navigate(`/squadflix/live-event/${item.idEvent}/0`);
                                } else {
                                    setSelectedEvent(item);
                                }
                            }}
                            onSecondaryAction={
                                hasLinks ? () => setSelectedEvent(item) : undefined
                            }
                            secondaryActionLabel="Elegir canal"
                        />
                    );
                })}
            </TvMediaRow>

            <TvModal
                open={!!selectedEvent}
                onClose={() => setSelectedEvent(null)}
                title={selectedEvent?.strEvent}
                subtitle={`Selecciona un enlace para reproducir (${selectedEvent?.acestreamEvent?.enlaces?.length || 0} disponibles)`}>
                <div className="tv-modal-list">
                    {selectedEvent?.acestreamEvent?.enlaces?.map((link, idx) => (
                        <button
                            key={idx}
                            type="button"
                            data-tv-focusable="true"
                            data-tv-autofocus={idx === 0 ? "true" : undefined}
                            className="tv-modal-option-btn"
                            onClick={() => {
                                const evId = selectedEvent.idEvent;
                                setSelectedEvent(null);
                                navigate(`/squadflix/live-event/${evId}/${idx}`);
                            }}>
                            <span>
                                <i className="fa-solid fa-tower-broadcast pe-3 text-danger"></i>
                                {link.name || `Opción ${idx + 1}`}
                            </span>
                            <i className="fa-solid fa-play"></i>
                        </button>
                    ))}
                </div>
            </TvModal>
        </>
    );
};

const NewEpisodesTvRow: React.FC<RowCommonProps> = (props) => {
    const {series, loading} = useNewEpisodesData();
    const squadflixNav = UseSquadflixNavigate({});
    const {userPreferences, loading: loadingPreferences} = useUserPreferences();
    const intl = useIntl();

    if (loading || !series.length) return null;

    return (
        <TvMediaRow
            {...props}
            title={intl.formatMessage({id: "squadflix-home.new-episodes"})}
            icon={<i className="fa-regular fa-calendar-days"></i>}
            count={series.length}>
            {series.map((item: any) => {
                const episode = item.next_episode_to_air;
                if (!episode) return null;
                const airDate = new Date(episode.air_date);
                const airedToday = dateIsToday(airDate);
                const isSpoiler =
                    userPreferences.hideSpoilers || userPreferences.hideSpoilers == null || loadingPreferences;

                return (
                    <TvMediaCard
                        key={item.id}
                        width="340px"
                        aspectRatio="16/9"
                        tmdbSrc={item.backdrop_path}
                        showPlayOverlay={airedToday}
                        badgeTopLeft={airedToday ? "Estreno Hoy" : `Estreno ${airDate.toLocaleDateString()}`}
                        meta={
                            airedToday
                                ? intl.formatMessage({id: "squadflix-home.new-episodes.see-now"})
                                : `Disponible el ${airDate.toLocaleDateString()}`
                        }
                        title={item.name}
                        subtitle={
                            isSpoiler
                                ? `Temporada ${episode.season_number} • Episodio ${episode.episode_number}`
                                : `S${episode.season_number}:E${episode.episode_number} — ${episode.name}`
                        }
                        onClick={() => {
                            if (airedToday) {
                                squadflixNav.navigateToEpisode(episode);
                            } else {
                                squadflixNav.navigateToSerie(item);
                            }
                        }}
                        onSecondaryAction={() => squadflixNav.navigateToSerie(item)}
                        secondaryActionLabel="Ficha de serie"
                    />
                );
            })}
        </TvMediaRow>
    );
};

const CollectionTvRow: React.FC<
    RowCommonProps & {
        collectionId: string;
        customTitle?: string;
        iconClass?: string;
        canHide?: boolean;
        onHide?: () => void;
    }
> = ({collectionId, customTitle, iconClass = "fa-solid fa-layer-group", canHide, onHide, ...props}) => {
    const {items, collectionInfo, initialLoading, loadNextPage} = useCollectionItemsData(collectionId);
    const squadflixNav = UseSquadflixNavigate({});

    if (initialLoading || !items.length) return null;

    const resolvedTitle = customTitle
        ? customTitle.replace("{{collectionName}}", collectionInfo?.name || "Colección")
        : collectionInfo?.name || "Colección";

    return (
        <TvMediaRow
            {...props}
            title={resolvedTitle}
            icon={<i className={iconClass}></i>}
            count={items.length}
            onHideRow={canHide ? onHide : undefined}
            onScrollEnd={loadNextPage}>
            {items.map((item: any) => {
                const numberOfSeasons = item.isMovie ? 0 : countItemTotalSeasons(item);
                return (
                    <TvMediaCard
                        key={`${item.id}_${item.isMovie}`}
                        width="220px"
                        aspectRatio="2/3"
                        tmdbSrc={item.poster_path || item.backdrop_path}
                        badgeTopLeft={
                            item.view ? (
                                <>
                                    <i className="fa-solid fa-eye pe-1"></i>Visto
                                </>
                            ) : undefined
                        }
                        badgeTopRight={
                            item.vote_average ? (
                                <>
                                    {item.vote_average.toFixed(1)} <i className="fa-solid fa-star text-warning"></i>
                                </>
                            ) : undefined
                        }
                        meta={
                            item.isMovie
                                ? `${getYearOfDate(item.release_date)} • ${item.runtime || "?"} min`
                                : `${getSerieDates(item)} • ${numberOfSeasons} temp.`
                        }
                        title={item.name ?? item.title}
                        onClick={() => {
                            if (item.isMovie) {
                                squadflixNav.navigateToMovie(item);
                            } else {
                                squadflixNav.navigateToSerie(item);
                            }
                        }}
                    />
                );
            })}
        </TvMediaRow>
    );
};

const TrendingMoviesTvRow: React.FC<RowCommonProps> = (props) => {
    const {movies, loading} = useTrendingMoviesData();
    const squadflixNav = UseSquadflixNavigate({});

    if (loading || !movies.length) return null;

    return (
        <TvMediaRow
            {...props}
            title="Películas en Tendencia"
            icon={<i className="fa-solid fa-fire-flame-curved"></i>}
            count={movies.length}>
            {movies.map((item: any) => (
                <TvMediaCard
                    key={item.id}
                    width="220px"
                    aspectRatio="2/3"
                    tmdbSrc={item.poster_path}
                    badgeTopRight={
                        item.vote_average ? (
                            <>
                                {item.vote_average.toFixed(1)} <i className="fa-solid fa-star text-warning"></i>
                            </>
                        ) : undefined
                    }
                    meta={getYearOfDate(item.release_date)}
                    title={item.title || item.name}
                    onClick={() => squadflixNav.navigateToMovie(item)}
                />
            ))}
        </TvMediaRow>
    );
};

const TrendingSeriesTvRow: React.FC<RowCommonProps> = (props) => {
    const {series, loading} = useTrendingSeriesData();
    const squadflixNav = UseSquadflixNavigate({});

    if (loading || !series.length) return null;

    return (
        <TvMediaRow
            {...props}
            title="Series en Tendencia"
            icon={<i className="fa-solid fa-chart-line"></i>}
            count={series.length}>
            {series.map((item: any) => (
                <TvMediaCard
                    key={item.id}
                    width="220px"
                    aspectRatio="2/3"
                    tmdbSrc={item.poster_path}
                    badgeTopRight={
                        item.vote_average ? (
                            <>
                                {item.vote_average.toFixed(1)} <i className="fa-solid fa-star text-warning"></i>
                            </>
                        ) : undefined
                    }
                    meta={getYearOfDate(item.first_air_date)}
                    title={item.name || item.title}
                    onClick={() => squadflixNav.navigateToSerie(item)}
                />
            ))}
        </TvMediaRow>
    );
};

const LiveChannelsTvRow: React.FC<RowCommonProps> = (props) => {
    const {channels, loading} = useStremioChannelsData();
    const navigate = useNavigate();

    if (loading || !channels.length) return null;

    return (
        <TvMediaRow
            {...props}
            title="Canales de TV en Directo"
            icon={<i className="fa-solid fa-tower-broadcast"></i>}
            count={channels.length}
            extraHeaderActions={
                <button
                    type="button"
                    data-tv-focusable="true"
                    className="tv-btn tv-btn--ghost tv-btn--sm"
                    onClick={() => navigate("/squadflix/live")}>
                    Ver todos los canales <i className="fa-solid fa-chevron-right ps-2"></i>
                </button>
            }>
            {channels.map((channel) => (
                <TvMediaCard
                    key={channel.id}
                    width="210px"
                    aspectRatio="1/1"
                    cachedSrc={channel.poster}
                    badgeTopRight={channel.streams ? `${channel.streams} enlaces` : undefined}
                    meta="Canal en directo"
                    title={channel.name}
                    onClick={() => navigate(`/squadflix/live/channel/${channel.id}`)}
                />
            ))}
        </TvMediaRow>
    );
};

const SquadflixOriginalsTvRow: React.FC<RowCommonProps> = (props) => {
    const {items, loading} = useSquadflixOriginalsData();
    const squadflixNav = UseSquadflixNavigate({});

    if (loading || !items.length) return null;

    return (
        <TvMediaRow
            {...props}
            title="SquadFlix Originals"
            icon={<i className="fa-solid fa-certificate"></i>}
            count={items.length}>
            {items.map((item: any) => (
                <TvMediaCard
                    key={`${item.id}_${item.isMovie}`}
                    width="220px"
                    aspectRatio="2/3"
                    tmdbSrc={item.poster_path}
                    badgeTopLeft="🌟 Original"
                    badgeTopRight={
                        item.vote_average ? (
                            <>
                                {item.vote_average.toFixed(1)} <i className="fa-solid fa-star text-warning"></i>
                            </>
                        ) : undefined
                    }
                    meta={getYearOfDate(item.first_air_date ?? item.release_date)}
                    title={item.name || item.title}
                    onClick={() =>
                        item.isMovie ? squadflixNav.navigateToMovie(item) : squadflixNav.navigateToSerie(item)
                    }
                />
            ))}
        </TvMediaRow>
    );
};

const TowersStreamTvRow: React.FC<RowCommonProps> = (props) => {
    const {items, loading} = useTorrestreamItemsData();
    const squadflixNav = UseSquadflixNavigate({});
    const navigate = useNavigate();
    const {hasPermission} = useChatStore();
    const intl = useIntl();

    if (loading || !items.length) return null;

    return (
        <TvMediaRow
            {...props}
            title={intl.formatMessage({id: "squadflix-home.towers-stream"})}
            icon={<i className="fa-solid fa-server"></i>}
            count={items.length}
            extraHeaderActions={
                hasPermission("towersstreamDashboard") ? (
                    <button
                        type="button"
                        data-tv-focusable="true"
                        className="tv-btn tv-btn--ghost tv-btn--sm"
                        onClick={() => navigate("/squadflix/towersstream/dash")}>
                        <i className="fa-solid fa-gauge-high pe-2"></i>Dashboard Torrents
                    </button>
                ) : undefined
            }>
            {items.map((item: any) => (
                <TvMediaCard
                    key={`${item.id}_${item.isMovie}`}
                    width="220px"
                    aspectRatio="2/3"
                    tmdbSrc={item.poster_path}
                    badgeTopRight={
                        item.vote_average ? (
                            <>
                                {item.vote_average.toFixed(1)} <i className="fa-solid fa-star text-warning"></i>
                            </>
                        ) : undefined
                    }
                    meta={getYearOfDate(item.first_air_date ?? item.release_date)}
                    title={item.name || item.title}
                    onClick={() =>
                        item.isMovie ? squadflixNav.navigateToMovie(item) : squadflixNav.navigateToSerie(item)
                    }
                />
            ))}
        </TvMediaRow>
    );
};

const SquadflixTvHome: React.FC = () => {
    const {userPreferences, updatePreference} = useUserPreferences();
    const navigate = useNavigate();
    const intl = useIntl();
    const preferences = userPreferences?.squadflixHomePreferences;

    const itemsRowsHook = UseSquadflixItemsRows({
        preferences,
        collections: userPreferences?.squadflixHomeCollections
    });

    const {
        allItems: heroItems,
        currentIndex: heroIndex,
        setCurrentIndex: setHeroIndex,
        currentItem: heroItem,
        nextSlide,
        prevSlide,
        openItem
    } = useTrendingHeaderItems(preferences?.headerType, 8000);

    const handleShowAddCollection = (collection: any) => {
        if (!collection?.id) return;
        if (userPreferences.squadflixHomeCollections?.includes(collection.id)) return;
        updatePreference("squadflixHomeCollections", [
            ...(userPreferences.squadflixHomeCollections || []),
            collection.id
        ]);
    };

    const handleHideCollection = (collectionId: string) => {
        const nextCollections = (userPreferences.squadflixHomeCollections || []).filter(
            (id: string) => id !== collectionId
        );
        updatePreference("squadflixHomeCollections", nextCollections);
    };

    if (!auth.currentUser || !preferences) {
        return null;
    }

    const heroBackdrop = heroItem?.backdrop_path ?? heroItem?.backgroundUrl;
    const heroLogo = heroItem?.logo_path ?? heroItem?.logoImageUrl;
    const heroTitle = heroItem?.title || heroItem?.name || "SquadFlix TV";
    const heroIsMovie = heroItem?.isMovie || heroItem?.media_type === "movie" || heroItem?.type === "movies";

    return (
        <div className="squadflix-tv-home">
            {/* Hero Spotlight 16:9 */}
            {heroItem && (
                <section className="tv-hero-spotlight" data-tv-row="tv-hero">
                    <div className="tv-hero-spotlight__backdrop">
                        {heroBackdrop && (
                            <TmdbImage
                                src={heroBackdrop}
                                size="original"
                                className="tv-hero-spotlight__img"
                            />
                        )}
                        <div className="tv-hero-spotlight__vignette" />
                    </div>

                    <div className="tv-hero-spotlight__content">
                        <div className="tv-hero-spotlight__badges">
                            <span className="tv-pill tv-pill--accent">
                                <i className="fa-solid fa-clapperboard pe-2"></i>
                                {heroItem.order ? "Destacado SquadFlix" : heroIsMovie ? "Película" : "Serie"}
                            </span>
                            {typeof heroItem.vote_average === "number" && (
                                <span className="tv-pill">
                                    <i className="fa-solid fa-star text-warning pe-1"></i>
                                    {heroItem.vote_average.toFixed(1)}
                                </span>
                            )}
                            {(heroItem.release_date || heroItem.first_air_date) && (
                                <span className="tv-pill">
                                    {getYearOfDate(heroItem.release_date || heroItem.first_air_date)}
                                </span>
                            )}
                        </div>

                        {heroLogo ? (
                            <TmdbImage
                                src={heroLogo}
                                className="tv-hero-spotlight__logo"
                            />
                        ) : (
                            <h1 className="tv-hero-spotlight__title">{heroTitle}</h1>
                        )}

                        {(heroItem.subtitle || heroItem.overview) && (
                            <p className="tv-hero-spotlight__overview">
                                {heroItem.subtitle || heroItem.overview}
                            </p>
                        )}

                        <div className="tv-hero-spotlight__actions">
                            <button
                                type="button"
                                data-tv-focusable="true"
                                data-tv-autofocus="true"
                                className="tv-btn tv-btn--primary tv-btn--lg"
                                onClick={() => openItem(heroItem)}>
                                <i className="fa-solid fa-play pe-2"></i>Ver ahora
                            </button>
                            <button
                                type="button"
                                data-tv-focusable="true"
                                className="tv-btn tv-btn--glass"
                                onClick={() => navigate("/squadflix/search-tmdb")}>
                                <i className="fa-solid fa-magnifying-glass pe-2"></i>Buscar
                            </button>
                            <button
                                type="button"
                                data-tv-focusable="true"
                                className="tv-btn tv-btn--glass"
                                onClick={() => navigate("/squadflix/live")}>
                                <i className="fa-solid fa-tower-broadcast pe-2"></i>En Vivo
                            </button>
                            <button
                                type="button"
                                data-tv-focusable="true"
                                className="tv-btn tv-btn--ghost tv-btn--icon"
                                onClick={prevSlide}
                                title="Anterior destacado">
                                <i className="fa-solid fa-chevron-left"></i>
                            </button>
                            <button
                                type="button"
                                data-tv-focusable="true"
                                className="tv-btn tv-btn--ghost tv-btn--icon"
                                onClick={nextSlide}
                                title="Siguiente destacado">
                                <i className="fa-solid fa-chevron-right"></i>
                            </button>
                        </div>

                        {heroItems.length > 1 && (
                            <div className="tv-hero-spotlight__dots">
                                {heroItems.map((_: any, idx: number) => (
                                    <button
                                        key={idx}
                                        type="button"
                                        tabIndex={-1}
                                        onClick={() => setHeroIndex(idx)}
                                        className={`tv-hero-spotlight__dot ${idx === heroIndex ? "is-active" : ""}`}
                                    />
                                ))}
                                <span className="tv-hero-spotlight__dots-counter">
                                    {heroIndex + 1} / {heroItems.length}
                                </span>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* Filas dinámicas del catálogo */}
            <div className="squadflix-tv-home__rows">
                {itemsRowsHook.itemsRows.map((row, index) => {
                    const commonProps: RowCommonProps = {
                        rowId: `tv-row-${row.id}`,
                        order: row.order,
                        showReorderControls: itemsRowsHook.ableReorder,
                        isFirst: index === 0,
                        isLast: index === itemsRowsHook.itemsRows.length - 1,
                        onMoveUp: () => itemsRowsHook.moveItemRow(row.id, "up"),
                        onMoveDown: () => itemsRowsHook.moveItemRow(row.id, "down")
                    };

                    switch (row.id) {
                        case SquadflixItemRowId.CONTINUE_WATCHING:
                            return <ContinueWatchingTvRow key={row.id} {...commonProps} />;
                        case SquadflixItemRowId.SPANISH_LEAGUE_EVENTS:
                            return <SpanishLeagueEventsTvRow key={row.id} {...commonProps} />;
                        case SquadflixItemRowId.NEW_EPISODES:
                            return <NewEpisodesTvRow key={row.id} {...commonProps} />;
                        case SquadflixItemRowId.VIEW_LATER:
                            return (
                                <CollectionTvRow
                                    key={row.id}
                                    {...commonProps}
                                    collectionId={"view-later-" + auth.currentUser.uid}
                                    customTitle={intl.formatMessage({id: "squadflix-home.view-later"})}
                                    iconClass="fa-solid fa-clock-rotate-left fa-flip-horizontal"
                                />
                            );
                        case SquadflixItemRowId.TRENDING_MOVIES:
                            return <TrendingMoviesTvRow key={row.id} {...commonProps} />;
                        case SquadflixItemRowId.TRENDING_SERIES:
                            return <TrendingSeriesTvRow key={row.id} {...commonProps} />;
                        case SquadflixItemRowId.LIVE_CHANNELS:
                            return <LiveChannelsTvRow key={row.id} {...commonProps} />;
                        case SquadflixItemRowId.SQUADFLIX_ORIGINALS:
                            return <SquadflixOriginalsTvRow key={row.id} {...commonProps} />;
                        case SquadflixItemRowId.TOWERSTREAM_ITEMS:
                            return <TowersStreamTvRow key={row.id} {...commonProps} />;
                        default:
                            return (
                                <CollectionTvRow
                                    key={row.id}
                                    {...commonProps}
                                    collectionId={row.id}
                                    canHide
                                    onHide={() => handleHideCollection(row.id)}
                                />
                            );
                    }
                })}
            </div>

            {/* Barra inferior de personalización TV */}
            <div className="squadflix-tv-home__footer-actions" data-tv-row="tv-home-footer">
                <TmdbCollectionSelector
                    disabledCollections={userPreferences.squadflixHomeCollections || []}
                    actionText="Mostrar"
                    onSelected={handleShowAddCollection}
                    customBtn={
                        <span>
                            <i className="fa-solid fa-layer-group pe-2"></i>Mostrar colección en Inicio
                        </span>
                    }
                />
                <button
                    type="button"
                    data-tv-focusable="true"
                    className={`tv-btn ${itemsRowsHook.ableReorder ? "tv-btn--primary" : "tv-btn--glass"}`}
                    onClick={() => itemsRowsHook.setAbleReorder(!itemsRowsHook.ableReorder)}>
                    <i className={`fa-solid ${itemsRowsHook.ableReorder ? "fa-check" : "fa-sort"} pe-2`}></i>
                    {itemsRowsHook.ableReorder ? "Terminar de ordenar filas" : "Reordenar filas"}
                </button>
            </div>

            <TmdbCredits />
        </div>
    );
};

export default SquadflixTvHome;
